import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import { Helmet } from 'react-helmet-async';

type RehabSectionProps = {
  id: string;
  className: string;
  title: string;
  imageAlt: string;
  imageSrc?: string;
  children: ReactNode;
};

function RehabSection({
  id,
  className,
  title,
  imageAlt,
  imageSrc,
  children,
}: RehabSectionProps) {
  const image = (
    <div className="section__image">
      {imageSrc ? (
        <img src={imageSrc} alt={imageAlt} />
      ) : (
        <div className="rehabilitation-image-placeholder" role="img" aria-label={imageAlt} />
      )}
    </div>
  );

  return (
    <section id={id} className={`content-section rehabilitation-block ${className}`}>
      <div className="section__container">
        <div className="section__text">
          <h2>{title}</h2>
          {children}
        </div>
        {image}
      </div>
    </section>
  );
}

function Rehabilitation() {
  return (
    <>
      <Helmet>
        <title>Physical Rehab Centre | Graham Care</title>
        <meta
          name="description"
          content="CQC-registered inpatient rehabilitation at Whitstable House Care Suites — structured recovery with specialist equipment and 24-hour nurse-led care."
        />
      </Helmet>

      <div className="page rehabilitation-page">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section rehabilitation-page-header">
            <div className="rehabilitation-container">
              <h1>Physical Rehab Centre</h1>
              <p className="rehab-subtitle">CQC-registered physical rehabilitation centre at Whitstable House</p>
            </div>
          </section>

          <section className="content-section rehabilitation-overview rehab-intro">
            <div className="section__container">
              <div className="section__text">
                <h2>Service Overview</h2>
                <p>
                  The Physical Rehab Centre is a CQC-registered, accommodation-based rehabilitation service providing
                  structured, intensive recovery programmes for adults requiring more than sessional or home-based
                  physiotherapy. The service is designed to support early discharge from acute hospital settings;
                  post-operative orthopaedic recovery; neurological rehabilitation; cardiac and respiratory
                  reconditioning; frailty and functional decline; sports injury rehabilitation; and women&apos;s health
                  rehabilitation. The unit operates within Whitstable House Care Suites and is supported by 24-hour,
                  nurse-led clinical care.
                </p>
              </div>
              <div className="section__image">
                <img src="/Resident_smiling.png" alt="Whitstable House Care Suites" />
              </div>
            </div>
          </section>

          <RehabSection
            id="clinical-governance"
            className="rehab-governance"
            title="Clinical Governance & Professional Standards"
            imageAlt="Clinical care at Whitstable House"
            imageSrc="/iStock-1165981632-scaled.jpg"
          >
            <p>
              The rehabilitation service is led by a Chartered Physiotherapist (MCSP), registered with the Health and
              Care Professions Council (HCPC), with 14 years&apos; clinical experience across NHS and private practice.
              Chartered physiotherapists are active members of the Chartered Society of Physiotherapy and must maintain
              ongoing CPD, retain a professional portfolio, practise in accordance with evidence-based guidelines, and
              adhere to strict professional and ethical standards. This ensures high-quality, safe, up-to-date
              rehabilitation practice. The unit is fully CQC-registered as an accommodation-based service, with 24/7
              in-house registered nursing support.{' '}
              <Link to="/nursing-care" className="nurse-care-link">
                Clinical care at Whitstable House
              </Link>
            </p>
          </RehabSection>

          <RehabSection
            id="specialist-equipment"
            className="rehab-equipment"
            title="Specialist Rehabilitation Equipment"
            imageAlt="Rehabilitation facilities"
            imageSrc="/Anti_Gravity_3(1).jpeg"
          >
            <ul>
              <li className="rehabilitation-list-lead">
                The service includes access to advanced rehabilitation technology rarely available outside hospital
                settings:
              </li>
              <li>
                <strong>AlterG Anti-Gravity Treadmill</strong> — Precise body-weight support, early weight-bearing
                post-surgery, gait retraining and symmetry analysis, fall-safe mobilisation.
              </li>
              <li>
                <strong>HUR SmartBalance System</strong> — Objective balance assessment, fall-risk identification,
                cognitive-motor training, progress monitoring.
              </li>
              <li>
                <strong>HUR Multifunction Rehabilitation Machines</strong> — Controlled, low-impact strength
                rebuilding, multi-muscle group training, range-limited safe joint rehabilitation, designed specifically
                for rehabilitation populations.
              </li>
              <li>
                <strong>&quot;The Shuffler&quot;</strong> — Helps prevent falls, encourages low-impact rebuilding of
                muscle strength and tone, keeps individuals mobile for as long as possible, can be used whilst seated.
              </li>
            </ul>
          </RehabSection>

          <RehabSection
            id="rehabilitation-model"
            className="rehab-model"
            title="Rehabilitation Model"
            imageAlt="Private accommodation for rehabilitation"
            imageSrc="/Nurse_With_Resident.png"
          >
            <p>
              <strong>Inpatient Rehabilitation</strong> — Patients stay in private accommodation while receiving
              structured daily physiotherapy, equipment-led progression, continuous clinical observation, medication
              oversight and multidisciplinary coordination. This model bridges the gap between acute hospital care and
              independent living.
            </p>
            <p>
              <strong>Orthopaedic Early Recovery Pathway</strong> — For many hip and knee replacements, patients may
              transfer directly from hospital; mobilisation can begin immediately within a supported environment;
              hospital length of stay may be reduced. We work collaboratively with discharge teams and consultants.
            </p>
            <p>
              <strong>Outpatient Continuation</strong> — Following inpatient rehabilitation, patients may continue
              structured therapy as outpatients, access specialist equipment, undergo reassessment and progression
              planning, or return for further rehabilitation blocks if required.
            </p>
          </RehabSection>

          <RehabSection
            id="patient-pathway"
            className="rehab-pathway"
            title="Patient Pathway"
            imageAlt="Welcome and assessment"
            imageSrc="/Physio_1(1).jpeg"
          >
            <p>
              On admission, patients receive: comprehensive subjective and objective assessment; movement, strength and
              balance evaluation; clear explanation of findings; collaborative goal-setting; and an individually
              prescribed programme. Treatment plans are realistic, lifestyle-focused and progression-based. Where
              appropriate, patients may be referred onward for consultant review, diagnostic imaging (X-ray, MRI), or
              additional specialist input.
            </p>
          </RehabSection>

          <RehabSection
            id="referral-pathways"
            className="rehab-referral"
            title="Referral Pathways"
            imageAlt="Referral and welcome"
            imageSrc="/Staff_talking.png"
          >
            <p>
              <strong>NHS Physiotherapy</strong> — Requires GP or consultant referral.{' '}
              <strong>The Physical Rehab Centre</strong> — No GP referral required for private attendance. Patients may
              self-refer directly. If using private medical insurance, most insurers do not require GP referral; patients
              should confirm individual policy requirements. We welcome referrals from: Consultants, GPs, hospital
              discharge teams, case managers and private insurers.{' '}
              <Link to="/contact-us" className="nurse-care-link">
                Contact us about rehabilitation
              </Link>
            </p>
          </RehabSection>
        </main>

        <footer className="footer">
          <section className="footer__enquiry">
            <div className="footer__enquiry-container">
              <Link to="/contact-us" className="footer__enquiry-link">
                Enquire about Graham Care <span className="footer__arrow">▶</span>
              </Link>
            </div>
          </section>
          <div className="footer__container">
            <section className="contact-section">
              <div className="contact-section__content">
                <p>
                  For rehabilitation enquiries, please{' '}
                  <Link to="/contact-us" className="nurse-care-link">
                    contact us
                  </Link>
                  .
                </p>
                <p>Copyright © Graham Care Ltd</p>
              </div>
            </section>
          </div>
        </footer>
      </div>
    </>
  );
}

export default Rehabilitation;
